import React, { useState, useEffect } from 'react';
import { getReviewDetail, createReviewDetail, updateReviewDetail } from '../../services/api';

function ReviewInformation({ versionId, currentVersion, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewDetailId, setReviewDetailId] = useState(null);
  const [formData, setFormData] = useState({
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    contactEmail: '',
    demoAccountRequired: false,
    demoAccountName: '',
    demoAccountPassword: '',
    notes: ''
  });

  useEffect(() => {
    if (currentVersion) {
      loadReviewDetail();
    }
  }, [currentVersion]);

  const loadReviewDetail = async () => {
    setLoading(true);
    try {
      console.log('Loading review detail for version:', currentVersion);
      console.log('Version ID:', currentVersion.id);
      console.log('Version type:', currentVersion.type);
      
      const response = await getReviewDetail(currentVersion.id);
      console.log('Review detail response:', response.data);
      
      if (response.data.success && response.data.reviewDetail) {
        const detail = response.data.reviewDetail;
        console.log('Review detail ID:', detail.id);
        console.log('Review detail attributes:', detail.attributes);
        setReviewDetailId(detail.id);
        setFormData({
          contactFirstName: detail.attributes?.contactFirstName || '',
          contactLastName: detail.attributes?.contactLastName || '',
          contactPhone: detail.attributes?.contactPhone || '',
          contactEmail: detail.attributes?.contactEmail || '',
          demoAccountRequired: detail.attributes?.demoAccountRequired || false,
          demoAccountName: detail.attributes?.demoAccountName || '',
          demoAccountPassword: detail.attributes?.demoAccountPassword || '',
          notes: detail.attributes?.notes || ''
        });
      } else if (response.data.success === false && response.data.error) {
        console.error('Review detail not available:', response.data.error);
        setReviewDetailId(null);
      } else {
        console.log('No review detail found in response');
        console.log('Full response:', JSON.stringify(response.data, null, 2));
        setReviewDetailId(null);
      }
    } catch (err) {
      console.error('Failed to load review detail:', err);
      console.error('Error details:', err.response?.data);
      setReviewDetailId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!currentVersion) {
      alert('No version selected. Please select a version first.');
      return;
    }

    if (!formData.contactFirstName || !formData.contactLastName || 
        !formData.contactPhone || !formData.contactEmail) {
      alert('Please fill in all contact information fields');
      return;
    }

    if (formData.demoAccountRequired && 
        (!formData.demoAccountName || !formData.demoAccountPassword)) {
      alert('Please provide demo account credentials');
      return;
    }

    setSaving(true);
    try {
      // If reviewDetailId doesn't exist, try to fetch or create it
      let detailIdToUse = reviewDetailId;
      
      if (!detailIdToUse) {
        console.log('Review detail ID not cached, fetching from API...');
        console.log('Current version:', currentVersion);
        const response = await getReviewDetail(currentVersion.id);
        console.log('Fetch review detail response:', response.data);
        
        if (response.data.success && response.data.reviewDetail) {
          detailIdToUse = response.data.reviewDetail.id;
          setReviewDetailId(detailIdToUse);
          console.log('Review detail ID fetched:', detailIdToUse);
        } else {
          // Review detail doesn't exist - create it!
          console.log('Review detail does not exist, creating it...');
          const createResponse = await createReviewDetail(currentVersion.id, formData);
          console.log('Create review detail response:', createResponse.data);
          
          if (createResponse.data.success && createResponse.data.reviewDetail) {
            detailIdToUse = createResponse.data.reviewDetail.id;
            setReviewDetailId(detailIdToUse);
            console.log('Review detail created with ID:', detailIdToUse);
            alert('Review information created successfully!');
            onUpdate();
            return; // Exit early since we just created it
          } else if (createResponse.data.error) {
            // Show Apple's actual error
            const appleError = createResponse.data.error;
            const errorDetails = appleError.details?.[0];
            const errorMessage = errorDetails?.detail || appleError.message || 'Could not create review detail';
            alert('Apple API Error: ' + errorMessage);
            return;
          }
        }
      }

      if (!detailIdToUse) {
        alert('Could not get or create review detail.');
        return;
      }

      // Update the existing review detail
      console.log('Updating review detail with ID:', detailIdToUse);
      await updateReviewDetail(detailIdToUse, formData);
      alert('Review information saved successfully!');
      onUpdate();
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      
      // Extract and show Apple's actual error message
      const errorData = err.response?.data?.error;
      let errorMsg = 'Failed to save review information';
      
      if (errorData) {
        if (errorData.details && errorData.details.length > 0) {
          errorMsg = errorData.details[0].detail || errorData.details[0].title || errorMsg;
        } else if (errorData.message) {
          errorMsg = errorData.message;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      alert('Error: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <h2>App Review Information</h2>

      {currentVersion && (
        <div className={reviewDetailId ? 'alert alert-info' : 'alert alert-warning'} style={{ fontSize: '0.75rem', marginBottom: '1rem' }}>
          {reviewDetailId ? '✓' : '⚠️'} Review information for version: <strong>{currentVersion.attributes?.versionString}</strong> ({currentVersion.attributes?.appStoreState})
          {!reviewDetailId && (
            <div className="text-secondary" style={{ marginTop: '0.5rem', fontSize: '0.7rem' }}>
              <strong>Review detail not available yet.</strong><br />
              Apple creates this automatically, but it may take a few minutes after version creation.<br />
              Try refreshing this page in a few minutes, or check if the version is fully set up in App Store Connect.
            </div>
          )}
        </div>
      )}

      {!reviewDetailId && (
        <div className="alert alert-warning" style={{ marginBottom: '1.5rem' }}>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            <strong>Why is this not available?</strong><br />
            Apple's App Store Connect API automatically creates the review detail when a version is created, 
            but it may not be immediately available. This is normal for newly created versions.<br /><br />
            <strong>What to do:</strong>
          </p>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Wait a few minutes and try the button below</li>
            <li>Complete other sections (screenshots, localizations, build)</li>
            <li>Check App Store Connect directly to see if review information is editable there</li>
          </ul>
          <button
            className="btn btn-secondary"
            onClick={loadReviewDetail}
            disabled={loading}
            style={{ fontSize: '0.9rem' }}
          >
            {loading ? 'Checking...' : '🔄 Check Again'}
          </button>
        </div>
      )}

      <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
        Provide contact information and additional details to help the App Review team test your app.
      </p>

      <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', marginTop: '1.5rem' }}>
        Contact Information
      </h3>

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">First Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.contactFirstName}
            onChange={(e) => setFormData({ ...formData, contactFirstName: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Last Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.contactLastName}
            onChange={(e) => setFormData({ ...formData, contactLastName: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Phone Number *</label>
          <input
            type="tel"
            className="form-input"
            placeholder="+1 123 456 7890"
            value={formData.contactPhone}
            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Email *</label>
          <input
            type="email"
            className="form-input"
            placeholder="contact@example.com"
            value={formData.contactEmail}
            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
            required
          />
        </div>
      </div>

      <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', marginTop: '1.5rem' }}>
        Sign-In Information
      </h3>

      <div className="form-group">
        <div className="toggle-container">
          <label className="toggle">
            <input
              type="checkbox"
              checked={formData.demoAccountRequired}
              onChange={(e) => setFormData({ ...formData, demoAccountRequired: e.target.checked })}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="form-label" style={{ marginBottom: 0 }}>
            Sign-in required
          </span>
        </div>
        <small className="text-secondary" style={{ display: 'block', marginTop: '0.5rem' }}>
          Enable this if App Review needs to sign in to review your app
        </small>
      </div>

      {formData.demoAccountRequired && (
        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Demo Username *</label>
            <input
              type="text"
              className="form-input"
              value={formData.demoAccountName}
              onChange={(e) => setFormData({ ...formData, demoAccountName: e.target.value })}
              placeholder="demo@example.com"
              required={formData.demoAccountRequired}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Demo Password *</label>
            <input
              type="password"
              className="form-input"
              value={formData.demoAccountPassword}
              onChange={(e) => setFormData({ ...formData, demoAccountPassword: e.target.value })}
              placeholder="••••••••"
              required={formData.demoAccountRequired}
            />
          </div>
        </div>
      )}

      <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', marginTop: '1.5rem' }}>
        Notes for Review
      </h3>

      <div className="form-group">
        <label className="form-label">Additional Notes</label>
        <textarea
          className="form-textarea"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={6}
          maxLength={4000}
          placeholder="Include any additional information that will help during the review process, such as special configurations, non-obvious features, or testing instructions"
        />
        <small className="text-secondary">{formData.notes.length}/4000 characters</small>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Review Information'}
      </button>
    </div>
  );
}

export default ReviewInformation;
